import { ITEM_TYPES } from '../constants';
import {
    compoundMonths,
    daysBetween,
    debtBalanceAt,
    monthlyAmount,
    monthsBetween,
    num,
    toDate,
    resolveItemRates,
    stepItemMonth,
    stepLiquidMonths,
} from './engine';

// F28 — "Keep your plan fresh".
//
// A plan whose values are frozen goes stale, but silently drifting a user's
// numbers would be worse. So this module never changes anything: it rolls each
// value forward along the item's OWN growth assumptions from the moment it was
// last confirmed (`valueAsOf`) and returns a *estimate*. Applying it is an
// ordinary edit; dismissing it just re-anchors the confirmation date.
//
// Contract (do not weaken): every function takes an explicit end date and never
// calls `new Date()` internally. PLAN_SINCE_LAST_VISIT evaluates the portfolio
// at two different dates and diffs them — date purity makes that free.

// How stale a value must be before we say anything. `off` disables estimates.
export const STALENESS_THRESHOLDS = {
    day: 1,
    week: 7,
    month: 30,
    quarter: 91,
    off: null,
};
export const STALENESS_OPTIONS = ['day', 'week', 'month', 'quarter', 'off'];
export const DEFAULT_STALENESS_THRESHOLD = 'month';

// Relative move below which a estimate isn't worth the user's attention.
// Deliberately skipped at the `day` setting — someone who asks for daily
// nudges wants to see every move, however small.
export const DRIFT_THRESHOLD = 0.003;

// Salaries and living costs are step functions in reality, so their growth
// rates are much weaker evidence than an index fund's. Only ever ask about
// them once they're properly old.
export const INCOME_EXPENSE_MIN_MONTHS = 6;

// Which field carries the value we can roll, or null for types that don't roll
// (loans amortize on their own; see estimateDebtBalance).
export const getValueField = (item) => {
    switch (item?.type) {
        case ITEM_TYPES.REAL_ESTATE:
        case ITEM_TYPES.OTHER_INVESTMENT:
            return 'currentValue';
        case ITEM_TYPES.INCOME:
            return 'monthlyIncome';
        case ITEM_TYPES.EXPENSE:
            return 'monthlyExpense';
        default:
            return null;
    }
};

// Whole elapsed months step through the kernel — the same function the F1
// projection uses, so a estimate can never disagree with the chart about
// what a month does. The leftover part-month gets both growth and contribution
// pro-rata, matching how stepLiquidMonths pro-rates cashflow into its own
// remainder.
//
// The part-month contribution used to be dropped, on the reasoning that
// contributions land on discrete dates rather than continuously. That was wrong
// in its own terms: whole months already count contributions, so counting two
// and discarding the third partial one is not a policy, it is an omission. What
// it cost depended on where the anchor happened to fall — nothing at all when
// the remainder was small, but up to a full month's contribution when it sat
// just under a boundary (6.9 % of the estimate on the `edge` fixture's drawdown
// fund, reported as *larger* than the fund actually held). Fixed 2026-08-16.
const estimateInvestment = (value, item, months) => {
    const wholeMonths = Math.floor(months);
    const remainder = months - wholeMonths;
    const working = { ...item, currentValue: value };

    for (let i = 0; i < wholeMonths; i++) stepItemMonth(working);

    // Contribution first, then growth on the new balance — the order
    // stepItemMonth uses, so the tail behaves like a short month.
    const contribution = monthlyAmount(working.monthlyContribution, working.contributionFrequency) * remainder;
    const tailValue = Math.max(0, num(working.currentValue) + contribution);

    const tailRate = working.isGrowth ? resolveItemRates(working).growth : 0;
    return Math.max(0, compoundMonths(tailValue, tailRate, remainder));
};

/**
 * The value this item would have now if its own assumptions had simply played
 * out since it was last confirmed. Returns null when the item can't roll.
 */
export const estimateItemValue = (item, from, to) => {
    const field = getValueField(item);
    if (!field) return null;

    const months = monthsBetween(from, to);
    if (months <= 0) return null;

    const value = num(item[field]);

    switch (item.type) {
        case ITEM_TYPES.OTHER_INVESTMENT:
            return estimateInvestment(value, item, months);
        case ITEM_TYPES.REAL_ESTATE:
            return Math.max(0, compoundMonths(value, num(item.annualAppreciationRate), months));
        case ITEM_TYPES.INCOME:
        case ITEM_TYPES.EXPENSE:
            return Math.max(0, compoundMonths(value, num(item.annualGrowthRate), months));
        default:
            return null;
    }
};

/**
 * Cash rolls the way the projection's liquid line does: the portfolio's net
 * monthly cashflow lands in it, and the balance earns its interest rate.
 * `monthlyNetCashflow` must EXCLUDE liquid interest or it gets counted twice.
 */
export const estimateLiquidAssets = (amount, annualRate, monthlyNetCashflow, from, to) => {
    const months = monthsBetween(from, to);
    if (months <= 0) return null;
    return stepLiquidMonths(amount, annualRate, monthlyNetCashflow, months);
};

/**
 * A loan's or mortgage's outstanding balance at an arbitrary date. Debt already
 * rolls forward everywhere in the app (analytics and projection both amortize
 * from the start date to "now"), so this never produces a estimate — it
 * exists so PLAN_SINCE_LAST_VISIT can value net worth at two dates.
 */
export const estimateDebtBalance = (item, at) => debtBalanceAt(item, at);

const driftOf = (confirmed, estimated) => {
    if (confirmed === estimated) return 0;
    if (!confirmed) return estimated > 0 ? Infinity : -Infinity;
    return (estimated - confirmed) / Math.abs(confirmed);
};

const thresholdDays = (threshold) => {
    const days = STALENESS_THRESHOLDS[threshold];
    return typeof days === 'number' ? days : null;
};

/**
 * Everything the UI needs to offer a estimate, or an empty array when nothing
 * qualifies. Pure: pass `now` in.
 *
 * Each entry: { key, kind, itemId, item, field, name, type, confirmedValue,
 *               estimatedValue, drift, anchorDate, monthsStale, daysStale }
 */
export const buildEstimates = ({
    items = [],
    liquidAssets = 0,
    liquidAssetsInterestRate = 0,
    liquidAssetsAsOf = null,
    monthlyNetCashflow = 0,
    threshold = DEFAULT_STALENESS_THRESHOLD,
    now,
}) => {
    const minDays = thresholdDays(threshold);
    if (minDays === null) return [];

    const evaluatedAt = toDate(now);
    if (!evaluatedAt) return [];

    const skipDriftGate = threshold === 'day';
    const estimates = [];

    const qualifies = ({ confirmedValue, estimatedValue, daysStale, monthsStale, type }) => {
        if (estimatedValue == null || !isFinite(estimatedValue)) return false;
        if (daysStale < minDays) return false;
        if ((type === ITEM_TYPES.INCOME || type === ITEM_TYPES.EXPENSE) && monthsStale < INCOME_EXPENSE_MIN_MONTHS) return false;
        // Sub-currency-unit moves are noise whatever the settings say.
        if (Math.abs(estimatedValue - confirmedValue) < 1) return false;
        if (skipDriftGate) return true;
        return Math.abs(driftOf(confirmedValue, estimatedValue)) >= DRIFT_THRESHOLD;
    };

    items.forEach((item) => {
        const field = getValueField(item);
        if (!field) return;

        const anchorDate = toDate(item.valueAsOf);
        if (!anchorDate || anchorDate >= evaluatedAt) return; // absent anchor, or clock skew

        const confirmedValue = num(item[field]);
        const estimatedValue = estimateItemValue(item, anchorDate, evaluatedAt);
        const daysStale = daysBetween(anchorDate, evaluatedAt);
        const monthsStale = monthsBetween(anchorDate, evaluatedAt);

        if (!qualifies({ confirmedValue, estimatedValue, daysStale, monthsStale, type: item.type })) return;

        estimates.push({
            key: `item:${item.id}`,
            kind: 'item',
            itemId: item.id,
            item,
            field,
            name: item.name,
            type: item.type,
            confirmedValue,
            estimatedValue,
            drift: driftOf(confirmedValue, estimatedValue),
            anchorDate,
            monthsStale,
            daysStale,
        });
    });

    const liquidAnchor = toDate(liquidAssetsAsOf);
    if (liquidAnchor && liquidAnchor < evaluatedAt) {
        const confirmedValue = num(liquidAssets);
        const estimatedValue = estimateLiquidAssets(confirmedValue, liquidAssetsInterestRate, monthlyNetCashflow, liquidAnchor, evaluatedAt);
        const daysStale = daysBetween(liquidAnchor, evaluatedAt);
        const monthsStale = monthsBetween(liquidAnchor, evaluatedAt);

        if (qualifies({ confirmedValue, estimatedValue, daysStale, monthsStale, type: 'liquid' })) {
            estimates.push({
                key: 'liquid',
                kind: 'liquid',
                itemId: null,
                item: null,
                field: 'liquidAssets',
                name: null,
                type: 'liquid',
                confirmedValue,
                estimatedValue,
                drift: driftOf(confirmedValue, estimatedValue),
                anchorDate: liquidAnchor,
                monthsStale,
                daysStale,
            });
        }
    }

    return estimates.sort((a, b) => b.daysStale - a.daysStale);
};

// --- F28.1 — the portfolio review --------------------------------------------
//
// `buildEstimates` answers "what can we estimate?", which is a small slice of
// the portfolio: an item with no growth rate, or no anchor at all, can never
// produce an estimate and was therefore invisible on the review page. This
// answers the wider question the page actually asks — "what have I not
// confirmed lately?" — and hangs each estimate off the row it belongs to.
//
// It calls `buildEstimates` rather than reimplementing it, so the threshold,
// the drift gate and the estimate maths stay in exactly one place. That is also
// why `threshold: 'off'` blanks every `estimate` but leaves the list whole:
// the setting governs estimates, not the review.

const isSameLocalDay = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// Only so equally-aged rows keep their order between renders.
const byName = (a, b) => (a.name || '').localeCompare(b.name || '');

/**
 * Stalest first, with the rows that carry an estimate leading — they are the
 * only ones where one tap makes progress, so they ask the least of the user.
 * An unknown age sorts as infinitely old: an item with no anchor was last
 * touched before F28 shipped, so it is older than anything that has one.
 *
 * Written as explicit branches rather than as `Infinity - Infinity`, which is
 * NaN and would leave equally-unknown rows in an arbitrary order.
 */
const byReviewPriority = (a, b) => {
    if (!!a.estimate !== !!b.estimate) return a.estimate ? -1 : 1;
    if (a.ageUnknown !== b.ageUnknown) return a.ageUnknown ? -1 : 1;
    if (!a.ageUnknown && a.daysStale !== b.daysStale) return b.daysStale - a.daysStale;
    return byName(a, b);
};

/**
 * Every value in the plan, split into what the user can act on and what they
 * can't. Pure: pass `now` in.
 *
 *   needsReview   estimates first, then unknown age, then stalest → freshest
 *   updatedToday  anchored on today's local calendar date — read-only
 *   calculated    loans, whose balance amortizes on its own — nothing to confirm
 *
 * A ReviewRow is a superset of a estimate entry, so the same write handlers
 * serve both: { key, kind: 'item' | 'liquid' | 'debt', itemId, item, field,
 * name, type, confirmedValue, anchorDate, ageUnknown, daysStale, monthsStale,
 * estimate, mortgageBalance }.
 */
export const buildReviewList = ({
    items = [],
    liquidAssets = 0,
    liquidAssetsInterestRate = 0,
    liquidAssetsAsOf = null,
    monthlyNetCashflow = 0,
    threshold = DEFAULT_STALENESS_THRESHOLD,
    now,
}) => {
    const needsReview = [];
    const updatedToday = [];
    const calculated = [];
    const result = { needsReview, updatedToday, calculated };

    const evaluatedAt = toDate(now);
    if (!evaluatedAt) return result;

    const estimateByKey = {};
    buildEstimates({
        items, liquidAssets, liquidAssetsInterestRate, liquidAssetsAsOf,
        monthlyNetCashflow, threshold, now: evaluatedAt,
    }).forEach((estimate) => { estimateByKey[estimate.key] = estimate; });

    const buildRow = (row, anchor) => {
        const anchorDate = toDate(anchor);
        return {
            ...row,
            anchorDate,
            ageUnknown: !anchorDate,
            daysStale: anchorDate ? daysBetween(anchorDate, evaluatedAt) : 0,
            monthsStale: anchorDate ? monthsBetween(anchorDate, evaluatedAt) : 0,
            estimate: estimateByKey[row.key] || null,
        };
    };

    // A row settled during this session carries an anchor LATER than the pinned
    // `evaluatedAt` — as does a clock-skewed one. Both read as confirmed today.
    const file = (row) => {
        const confirmedToday = row.anchorDate
            && (row.anchorDate >= evaluatedAt || isSameLocalDay(row.anchorDate, evaluatedAt));
        (confirmedToday ? updatedToday : needsReview).push(row);
    };

    items.forEach((item) => {
        if (!item || item.isActive === false) return;

        // Loans amortize from their own start date, so their balance is always
        // current and there is nothing to confirm. Listed anyway, greyed, so
        // the page can honestly claim to cover the whole portfolio.
        if (item.type === ITEM_TYPES.LOAN) {
            calculated.push({
                key: `debt:${item.id}`,
                kind: 'debt',
                itemId: item.id,
                item,
                field: null,
                name: item.name,
                type: item.type,
                confirmedValue: debtBalanceAt(item, evaluatedAt),
                anchorDate: null,
                ageUnknown: false,
                daysStale: 0,
                monthsStale: 0,
                estimate: null,
                mortgageBalance: null,
            });
            return;
        }

        const field = getValueField(item);
        if (!field) return;

        // A mortgage amortizes on its own too, but it belongs to a property
        // whose market value IS the user's to confirm. One row per item: the
        // outstanding balance rides along as a note rather than as its own row.
        const mortgageBalance = item.type === ITEM_TYPES.REAL_ESTATE && item.hasMortgage
            ? debtBalanceAt(item, evaluatedAt) || null
            : null;

        file(buildRow({
            key: `item:${item.id}`,
            kind: 'item',
            itemId: item.id,
            item,
            field,
            name: item.name,
            type: item.type,
            confirmedValue: num(item[field]),
            mortgageBalance,
        }, item.valueAsOf));
    });

    file(buildRow({
        key: 'liquid',
        kind: 'liquid',
        itemId: null,
        item: null,
        field: 'liquidAssets',
        name: null,
        type: 'liquid',
        confirmedValue: num(liquidAssets),
        mortgageBalance: null,
    }, liquidAssetsAsOf));

    needsReview.sort(byReviewPriority);
    updatedToday.sort(byName);
    calculated.sort(byName);
    return result;
};
